import React, { useState, useEffect, useContext } from 'react';
import Draggable from 'react-draggable';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import axios from 'axios';
import { FaTimes, FaSpinner, FaRobot, FaExclamationTriangle, FaCheck, FaSave } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../context/AuthContext';

const CategoryInsightModal = ({ isOpen, onClose, category, assessment, initialPosition }) => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insights, setInsights] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [size, setSize] = useState({ width: 600, height: 700 });
  const [position, setPosition] = useState(initialPosition || { x: 100, y: 100 });
  const [selectedRecommendations, setSelectedRecommendations] = useState([]);
  const [savingRecommendations, setSavingRecommendations] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Fetch insights when modal opens
  useEffect(() => {
    if (isOpen && category && assessment) {
      fetchCategoryInsights();
      fetchSavedRecommendations();
    }
  }, [isOpen, category, assessment]);

  const fetchSavedRecommendations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/recommendations/${assessment._id}/${encodeURIComponent(category.name)}`, {
        headers: { 'x-auth-token': token }
      });
      
      if (response.data && response.data.recommendations) {
        setSelectedRecommendations(response.data.recommendations.map(rec => rec.text));
      }
    } catch (err) {
      console.error('Error fetching saved recommendations:', err);
      // Don't show error to user, just start with empty selections
      setSelectedRecommendations([]);
    }
  };

  const fetchCategoryInsights = async () => {
    setLoading(true);
    setError(null);
    setSaveSuccess(false);
    setSaveError(null);
    
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching insights for:', {
        assessmentId: assessment._id,
        category: category.name
      });
      
      const response = await axios.post('/api/openai/analyze-category', {
        assessmentId: assessment._id,
        category: category.name
      }, {
        headers: { 'x-auth-token': token },
        timeout: 30000 // 30 second timeout to prevent hanging requests
      });
      
      console.log('Insights response:', response.data);
      
      // Check if we received valid insights or fallback content
      if (response.data && response.data.analysis) {
        // Check if the response contains generic fallback content
        const isGenericContent = response.data.analysis.includes('Basic ' + category.name.toLowerCase() + ' practices are established');
        
        if (isGenericContent) {
          console.log('Received generic fallback content from API');
          setError({
            message: 'AI-generated insights are currently unavailable. Showing generic insights instead.',
            type: 'warning'
          });
        }
        
        setInsights({
          analysis: response.data.analysis,
          strengths: response.data.strengths || [],
          improvements: response.data.improvements || [],
          recommendations: response.data.recommendations || [],
          model: response.data.model || (isGenericContent ? 'fallback' : 'ai'),
          isGenerated: response.data.isGenerated !== false
        });
        
        // Set the last updated timestamp if available
        if (response.data.updatedAt) {
          setLastUpdated(new Date(response.data.updatedAt));
        } else if (response.data.createdAt) {
          setLastUpdated(new Date(response.data.createdAt));
        }
      } else {
        throw new Error('Invalid response format from API');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching category insights:', err);
      
      // Add fallback insights when API fails
      const fallbackInsights = generateFallbackInsights(category);
      setInsights({
        ...fallbackInsights,
        model: 'fallback',
        isGenerated: false
      });
      
      // Set a user-friendly error message
      setError({
        message: 'Unable to generate AI insights at this time. Showing generic insights instead.',
        type: 'error',
        details: err.message
      });
      
      setLoading(false);
    }
  };
  
  // Function to retry fetching insights
  const retryFetchInsights = () => {
    fetchCategoryInsights();
  };
  
  // Generate fallback insights when API fails
  const generateFallbackInsights = (category) => {
    const score = category.score || 0;
    const level = getMaturityLevel(score);
    
    return {
      analysis: `Your organization scores ${Math.round(score * 100)}% in the ${category.name} category, placing it at the ${level} maturity level. This indicates ${score < 0.5 ? 'significant room for improvement' : 'a solid foundation with opportunities to enhance capabilities'}.`,
      strengths: [
        `Basic ${category.name.toLowerCase()} practices are established`,
        `Some level of documentation exists for ${category.name.toLowerCase()} processes`,
        `Leadership awareness of ${category.name.toLowerCase()} importance is present`
      ],
      improvements: [
        `Enhance formal ${category.name.toLowerCase()} frameworks and policies`,
        `Increase staff training on ${category.name.toLowerCase()} best practices`,
        `Improve measurement and metrics for ${category.name.toLowerCase()} effectiveness`
      ],
      recommendations: [
        `Develop a comprehensive ${category.name.toLowerCase()} strategy aligned with business objectives`,
        `Implement regular ${category.name.toLowerCase()} assessments and reviews`,
        `Establish clear roles and responsibilities for ${category.name.toLowerCase()} management`,
        `Integrate ${category.name.toLowerCase()} considerations into business planning processes`
      ]
    };
  };

  const handleDrag = (e, data) => {
    setPosition({ x: data.x, y: data.y });
  };

  const handleRecommendationToggle = (recommendation) => {
    setSelectedRecommendations(prev => {
      if (prev.includes(recommendation)) {
        return prev.filter(rec => rec !== recommendation);
      } else {
        return [...prev, recommendation];
      }
    });
    
    // Reset save status when selections change
    setSaveSuccess(false);
    setSaveError(null);
  };
  
  const handleSaveRecommendations = async () => {
    if (selectedRecommendations.length === 0) {
      setSaveError(t('Please select at least one recommendation to save.'));
      return;
    }
    
    setSavingRecommendations(true);
    setSaveSuccess(false);
    setSaveError(null);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/recommendations', {
        assessmentId: assessment._id,
        category: category.name,
        recommendations: selectedRecommendations.map(text => ({
          text,
          status: 'Not Started',
          assignedTo: '',
          targetDate: '',
          priority: 'Medium',
          notes: ''
        }))
      }, {
        headers: { 'x-auth-token': token }
      });
      
      setSaveSuccess(true);
      setSavingRecommendations(false);
    } catch (err) {
      console.error('Error saving recommendations:', err);
      setSaveError(err.response?.data?.msg || t('Failed to save recommendations. Please try again.'));
      setSavingRecommendations(false);
    }
  };

  const handleResize = (e, { size }) => {
    setSize({ width: size.width, height: size.height });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={styles.overlay}>
      <Draggable
        handle=".modal-header"
        position={position}
        onDrag={handleDrag}
        bounds="parent"
      >
        <Resizable
          width={size.width}
          height={size.height}
          onResize={handleResize}
          minConstraints={[300, 300]}
          maxConstraints={[1000, 800]}
          resizeHandles={['se']}
        >
          <div className="modal-container" style={{
            ...styles.container,
            width: size.width,
            height: size.height
          }}>
            <div className="modal-header" style={styles.header}>
              <div style={styles.headerTitle}>
                <FaRobot style={{ marginRight: '10px' }} />
                <h3>AI Analysis: {category.name}</h3>
              </div>
              <button onClick={onClose} style={styles.closeButton}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-content" style={styles.content}>
              {loading ? (
                <div style={styles.loadingContainer}>
                  <FaSpinner style={styles.spinner} />
                  <p>Generating AI insights for {category.name}...</p>
                </div>
              ) : error ? (
                <div style={error.type === 'warning' ? styles.warningContainer : styles.errorContainer}>
                  <FaExclamationTriangle style={error.type === 'warning' ? styles.warningIcon : styles.errorIcon} />
                  <p>{error.message}</p>
                  {error.details && <p style={styles.errorDetails}>{error.details}</p>}
                  <button style={styles.retryButton} onClick={retryFetchInsights}>
                    Retry with AI
                  </button>
                </div>
              ) : (
                <div style={styles.insightsContainer}>
                  {/* Model info removed */}
                  
                  <div style={styles.scoreSection}>
                    <h4 style={styles.sectionTitle}>{t('Category Score')}</h4>
                    <div style={styles.scoreDisplay}>
                      <div style={{
                        ...styles.scoreCircle,
                        backgroundColor: getCategoryScoreColor(category.score),
                        color: getTextColorForBackground(getCategoryScoreColor(category.score))
                      }}>
                        {Math.round(category.score * 100)}%
                      </div>
                      <div style={styles.scoreText}>
                        <p style={styles.maturityLevel}>{getMaturityLevel(category.score || 0)}</p>
                        <p style={styles.scoreOutOf}>Maturity Level</p>
                      </div>
                    </div>
                  </div>
                  
                  <div style={styles.analysisSection}>
                    <h4 style={styles.sectionTitle}>{t('Analysis')}</h4>
                    <div style={styles.analysisText}>
                      {insights?.analysis ? (
                        <p>{insights.analysis}</p>
                      ) : (
                        <p>{t('No analysis available.')}</p>
                      )}
                      {lastUpdated && (
                        <p style={styles.timestamp}>Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}</p>
                      )}
                    </div>
                  </div>
                  
                  <div style={styles.strengthsSection}>
                    <h4 style={styles.sectionTitle}>{t('Strengths')}</h4>
                    <ul style={styles.strengthsList}>
                      {insights?.strengths && insights.strengths.length > 0 ? (
                        insights.strengths.map((strength, index) => (
                          <li key={index} style={styles.strengthItem}>{strength}</li>
                        ))
                      ) : (
                        <li style={styles.noItemsText}>{t('No strengths identified.')}</li>
                      )}
                    </ul>
                  </div>
                  
                  <div style={styles.improvementsSection}>
                    <h4 style={styles.sectionTitle}>{t('Areas for Improvement')}</h4>
                    <ul style={styles.improvementsList}>
                      {insights?.improvements && insights.improvements.length > 0 ? (
                        insights.improvements.map((improvement, index) => (
                          <li key={index} style={styles.improvementItem}>{improvement}</li>
                        ))
                      ) : (
                        <li style={styles.noItemsText}>{t('No improvement areas identified.')}</li>
                      )}
                    </ul>
                  </div>
                  
                  <div style={styles.recommendationsSection}>
                    <h4 style={styles.sectionTitle}>{t('Recommendations')}</h4>
                    <ul style={styles.recommendationsList}>
                      {insights?.recommendations && insights.recommendations.length > 0 ? (
                        insights.recommendations.map((recommendation, index) => (
                          <li key={index} style={styles.recommendationItem}>
                            <div style={styles.recommendationCheckboxContainer}>
                              <input 
                                type="checkbox" 
                                id={`recommendation-${index}`}
                                checked={selectedRecommendations.includes(recommendation)}
                                onChange={() => handleRecommendationToggle(recommendation)}
                                style={styles.recommendationCheckbox}
                              />
                              <label htmlFor={`recommendation-${index}`} style={styles.recommendationText}>
                                {recommendation}
                              </label>
                            </div>
                          </li>
                        ))
                      ) : (
                        <li style={styles.noItemsText}>{t('No recommendations available.')}</li>
                      )}
                    </ul>
                    
                    {insights?.recommendations && insights.recommendations.length > 0 && (
                      <div style={styles.saveRecommendationsContainer}>
                        <button 
                          onClick={handleSaveRecommendations} 
                          disabled={savingRecommendations}
                          style={styles.saveRecommendationsButton}
                        >
                          {savingRecommendations ? (
                            <>
                              <FaSpinner style={{...styles.spinner, marginRight: '8px'}} /> 
                              {t('Saving...')}
                            </>
                          ) : (
                            <>
                              <FaSave style={{marginRight: '8px'}} /> 
                              {t('Add to Roadmap')}
                            </>
                          )}
                        </button>
                        
                        {saveSuccess && (
                          <div style={styles.saveSuccessMessage}>
                            <FaCheck style={{marginRight: '5px', color: '#198754'}} />
                            {t('Recommendations saved successfully!')}
                          </div>
                        )}
                        
                        {saveError && (
                          <div style={styles.saveErrorMessage}>
                            {saveError}
                          </div>
                        )}
                        
                        <p style={styles.recommendationHelpText}>
                          {t('Selected recommendations will be added to your implementation roadmap.')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="resize-handle" style={styles.resizeHandle}>
              <div style={styles.resizeDots}></div>
            </div>
          </div>
        </Resizable>
      </Draggable>
    </div>
  );
};

// Helper functions
const getMaturityLevel = (score) => {
  // Ensure score is a valid number
  if (score === undefined || score === null || isNaN(score)) {
    score = 0;
  }
  
  const percentage = score * 100;
  if (percentage < 1) return 'None';
  if (percentage < 20) return 'Initial';
  if (percentage < 50) return 'Defined';
  if (percentage < 75) return 'Managed';
  if (percentage < 91) return 'Compliant';
  return 'Optimized';
};

const getCategoryScoreColor = (score) => {
  const percentage = score * 100;
  if (percentage >= 91) return '#198754'; // Optimized (91% - 100%) - Green
  if (percentage >= 75) return '#0D6EFD'; // Compliant (75% - 90%) - Blue
  if (percentage >= 50) return '#F4D03F'; // Managed (50% - 74%) - Softer Yellow
  if (percentage >= 20) return '#E67E22'; // Defined (20% - 49%) - Softer Orange
  if (percentage >= 1) return '#DC3545'; // Initial (1% - 19%) - Red
  return '#6C757D'; // None (0%) - Gray
};

// Get text color based on background color
const getTextColorForBackground = (backgroundColor) => {
  // Yellow gets navy text
  if (backgroundColor === '#F4D03F') {
    return '#243444';
  }
  // Dark backgrounds get white text
  const darkColors = ['#198754', '#0D6EFD', '#DC3545', '#6C757D', '#E67E22'];
  if (darkColors.includes(backgroundColor)) {
    return '#FFFFFF';
  }
  // Default
  return '#243444';
};

// Styles
const styles = {
  recommendationCheckbox: {
    width: '20px',
    height: '20px',
    marginRight: '15px',
    cursor: 'pointer',
    accentColor: '#243444'
  },
  recommendationCheckboxContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '10px'
  },
  recommendationText: {
    marginLeft: '5px',
    cursor: 'pointer',
    flex: 1
  },
  saveRecommendationsContainer: {
    marginTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  saveRecommendationsButton: {
    backgroundColor: '#243444',
    color: '#FFFAFA',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#1a2733'
    },
    '&:disabled': {
      backgroundColor: '#6c757d',
      cursor: 'not-allowed'
    }
  },
  saveSuccessMessage: {
    color: '#198754',
    marginTop: '10px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px'
  },
  saveErrorMessage: {
    color: '#7C1227',
    marginTop: '10px',
    fontSize: '14px'
  },
  recommendationHelpText: {
    color: '#4C5454',
    fontSize: '13px',
    marginTop: '10px',
    textAlign: 'center'
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  container: {
    backgroundColor: '#FFFAFA',
    borderRadius: '8px',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'absolute'
  },
  header: {
    backgroundColor: '#243444',
    color: '#D2BA7B',
    padding: '15px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'move',
    userSelect: 'none'
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#FFFAFA',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#4C5454'
  },
  spinner: {
    fontSize: '40px',
    color: '#D2BA7B',
    animation: 'spin 1s linear infinite',
    marginBottom: '15px'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#7C1227',
    textAlign: 'center',
    padding: '20px'
  },
  warningContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#E67E22',
    textAlign: 'center',
    padding: '20px',
    backgroundColor: 'rgba(230, 126, 34, 0.1)'
  },
  errorIcon: {
    fontSize: '40px',
    marginBottom: '15px',
    color: '#7C1227'
  },
  warningIcon: {
    fontSize: '40px',
    marginBottom: '15px',
    color: '#E67E22'
  },
  errorDetails: {
    fontSize: '12px',
    fontStyle: 'italic',
    marginTop: '10px',
    color: '#4C5454'
  },
  retryButton: {
    backgroundColor: '#243444',
    color: '#D2BA7B',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 20px',
    marginTop: '15px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  insightsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  scoreSection: {
    marginBottom: '20px'
  },
  scoreDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  scoreCircle: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FFFAFA',
    fontWeight: 'bold',
    fontSize: '20px'
  },
  scoreText: {
    display: 'flex',
    flexDirection: 'column'
  },
  maturityLevel: {
    fontWeight: 'bold',
    fontSize: '18px',
    color: '#243444',
    margin: 0
  },
  scoreOutOf: {
    color: '#4C5454',
    fontSize: '14px',
    margin: 0
  },
  sectionTitle: {
    color: '#243444',
    borderBottom: '2px solid #D2BA7B',
    paddingBottom: '8px',
    marginBottom: '15px',
    fontWeight: 'bold'
  },
  analysisText: {
    color: '#4C5454',
    lineHeight: '1.6',
    fontSize: '15px'
  },
  strengthsList: {
    listStyleType: 'none',
    padding: 0,
    margin: 0
  },
  strengthItem: {
    padding: '8px 0',
    color: '#4C5454',
    position: 'relative',
    paddingLeft: '25px',
    lineHeight: '1.4',
    fontSize: '15px',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: '0',
      top: '12px',
      width: '10px',
      height: '10px',
      backgroundColor: '#2E7D32',
      borderRadius: '50%'
    }
  },
  improvementsList: {
    listStyleType: 'none',
    padding: 0,
    margin: 0
  },
  improvementItem: {
    padding: '8px 0',
    color: '#4C5454',
    position: 'relative',
    paddingLeft: '25px',
    lineHeight: '1.4',
    fontSize: '15px',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: '0',
      top: '12px',
      width: '10px',
      height: '10px',
      backgroundColor: '#7C1227',
      borderRadius: '50%'
    }
  },
  recommendationsList: {
    listStyleType: 'none',
    padding: 0,
    margin: 0
  },
  recommendationItem: {
    padding: '8px 0',
    color: '#4C5454',
    position: 'relative',
    paddingLeft: '25px',
    lineHeight: '1.4',
    fontSize: '15px',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: '0',
      top: '12px',
      width: '10px',
      height: '10px',
      backgroundColor: '#D2BA7B',
      borderRadius: '50%'
    }
  },
  noItemsText: {
    color: '#4C5454',
    fontStyle: 'italic',
    fontSize: '14px'
  },
  timestamp: {
    color: '#4C5454',
    fontStyle: 'italic',
    fontSize: '12px',
    marginTop: '10px',
    textAlign: 'right'
  },
  resizeHandle: {
    position: 'absolute',
    bottom: '0',
    right: '0',
    width: '20px',
    height: '20px',
    cursor: 'se-resize'
  },
  resizeDots: {
    width: '10px',
    height: '10px',
    borderRight: '2px dotted #4C5454',
    borderBottom: '2px dotted #4C5454',
    position: 'absolute',
    bottom: '5px',
    right: '5px'
  },
  modelInfoContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginBottom: '10px'
  },
  aiBadge: {
    backgroundColor: '#198754',
    color: '#FFFAFA',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  fallbackBadge: {
    backgroundColor: '#E67E22',
    color: '#FFFAFA',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  modelBadge: {
    backgroundColor: '#243444',
    color: '#D2BA7B',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold'
  }
};

export default CategoryInsightModal;
